type UrlKind =
  | 'driveItem' // file (resolvable)
  | 'folder' // folder (resolvable)
  | 'shareLink' // short Office link like /:w:/... (resolvable)
  | 'sitePage' // SitePages/*.aspx (not a driveItem)
  | 'list' // Lists/* or short-link :l: (not a driveItem)
  | 'sharepointWeb' // other SharePoint page (not a driveItem)
  | 'external'; // non-SharePoint

export interface SharePointUrlClassification {
  kind: UrlKind;
  reason: string;
  host: string;
  href: string;
  // When resolvable, you can use /shares/{token} → driveItem
  resolver: 'sharesToken' | undefined;
  embedable: boolean; // whether it can be embedded in an iframe
}

/** Quick host checks */
function isSharePointHost(host: string) {
  return /\.sharepoint\.com$/i.test(host) || /\.sharepoint\.com\.cn$/i.test(host);
}
function isMySharePointHost(host: string) {
  // OneDrive for Business personal sites
  return /\.my\.sharepoint\.com$/i.test(host) || /\.sharepoint\.com\.cn$/i.test(host);
}

/** Normalize pathname for tests */
function segs(pathname: string): string[] {
  // Normalize repeated slashes globally, then split and decode each segment
  const normalized = pathname.replace(/\/+/g, '/');
  const segments = normalized.split('/').filter(Boolean);

  return segments.map((s) => {
    try {
      return decodeURIComponent(s);
    } catch {
      return s;
    }
  });
}

// Added 'l' for lists
const SHORT_LINK_CODES = new Set(['w', 'x', 'p', 'b', 'i', 'o', 'v', 'f', 'u', 't', 'l']);

function isShortLinkSegment(seg: string): false | string {
  // Matches codes like ":w:", ":x:", ":p:", ":b:", ":i:", ":o:", ":v:", ":f:", ":u:", ":t:", ":l:"
  const m = /^:([a-z]):$/i.exec(seg);
  if (!m) return false;
  const code = m[1].toLowerCase();

  return SHORT_LINK_CODES.has(code) ? code : false;
}

/** Main classifier */
export function classifySharePointUrl(input: string): SharePointUrlClassification {
  let u: URL;
  try {
    u = new URL(input);
  } catch {
    return {
      kind: 'external',
      reason: 'Not a valid absolute URL',
      host: '',
      href: input,
      resolver: undefined,
      embedable: false,
    };
  }

  const host = u.host.toLowerCase();
  if (!isSharePointHost(host)) {
    return {
      kind: 'external',
      reason: 'Non-SharePoint host',
      host,
      href: u.href,
      resolver: undefined,
      embedable: false,
    };
  }

  const parts = segs(u.pathname);
  const first = parts[0]?.toLowerCase() ?? '';

  // 1) Office short/guest links like "/:w:/s/...", "/:i:/r/...", "/:l:/s/..." (list)
  const shortCode = isShortLinkSegment(first);
  if (shortCode) {
    // Map certain short codes to specific kinds
    let kind: UrlKind;
    switch (shortCode) {
      case 'f':
        kind = 'folder';
        break;
      case 'l':
        kind = 'list';
        break;
      default:
        kind = 'shareLink';
        break;
    }

    return {
      kind,
      reason: `Office short link (:${shortCode}:)`,
      host,
      href: u.href,
      resolver: kind === 'shareLink' ? 'sharesToken' : undefined,
      embedable: kind === 'shareLink' ? true : false,
    };
  }

  // 2) Site Pages
  if (parts.includes('SitePages') || parts.includes('sitepages')) {
    return {
      kind: 'sitePage',
      reason: 'SharePoint Site Page (.aspx)',
      host,
      href: u.href,
      resolver: undefined,
      embedable: false,
    };
  }

  // 3) Lists
  if (parts.includes('Lists') || parts.includes('lists')) {
    return {
      kind: 'list',
      reason: 'SharePoint List view/item',
      host,
      href: u.href,
      resolver: undefined,
      embedable: false,
    };
  }
  if (parts.includes('_layouts') || parts.includes('_layouts/15')) {
    const isListy = u.searchParams.has('List') || u.searchParams.has('ListId');
    if (isListy) {
      return {
        kind: 'list',
        reason: 'Classic list page via _layouts',
        host,
        href: u.href,
        resolver: undefined,
        embedable: false,
      };
    }

    return {
      kind: 'sharepointWeb',
      reason: 'SharePoint system page (_layouts)',
      host,
      href: u.href,
      resolver: undefined,
      embedable: false,
    };
  }

  // 4) Library file/folder style URLs (often resolvable)
  const isLibraryish =
    parts.some((p) => /documents|doclib|documenten|shared/i.test(p)) ||
    parts.some((p) => /\.(docx?|xlsx?|pptx?|pdf|txt|csv|one|msg|zip)$/i.test(p));

  if (isLibraryish) {
    const looksLikeFile = /\.(\w{2,5})$/i.test(parts[parts.length - 1] || '');

    return {
      kind: looksLikeFile ? 'driveItem' : 'folder',
      reason: looksLikeFile ? 'Library file URL' : 'Library folder URL',
      host,
      href: u.href,
      resolver: 'sharesToken',
      embedable: looksLikeFile ? true : false,
    };
  }

  // 5) Personal OneDrive sites
  if (isMySharePointHost(host)) {
    return {
      kind: 'driveItem',
      reason: 'Likely OneDrive for Business item',
      host,
      href: u.href,
      resolver: 'sharesToken',
      embedable: true,
    };
  }

  // 6) Fallback
  return {
    kind: 'sharepointWeb',
    reason: 'SharePoint URL but not a file/folder/list/page pattern',
    host,
    href: u.href,
    resolver: undefined,
    embedable: false,
  };
}
